import React from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { Popper, Placements } from '@videoblocks/storywind';

import { selectIsEnterpriseMember } from '../../app/Search/selectors/searchSelectors';
import { selectIsMobile } from '../../auth/AuthSelectors';
import SiteConstants from '../../common/SiteConstants/SiteConstants';
import SignUpForFeaturePopover from '../../common/components/Popover/SignUpForFeaturePopover';
import { getPreviewUrl } from '../../common/utils';
import {
  selectCanDownloadPreviews,
  selectCanDownloadNonWatermarkedComps,
  selectDetailsStockItem,
  selectDetailsAcquisitionOption,
} from '../selectors/detailsSelectors';
import PreviewButton from './buttons/PreviewButton';

type Props = {
  telemetryKey?: string;
  children?: (downloadHandler: () => void) => React.ReactElement;
  iconOnLeft?: boolean;
  previewText?: string;
};

const StockItemDownloadButton = ({
  telemetryKey = 'preview.downloaded.details',
  iconOnLeft = true,
  previewText,
  children,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const stockItem = useSelector(selectDetailsStockItem);
  const isEnterpriseMember = useSelector(selectIsEnterpriseMember);
  const isMobile = useSelector(selectIsMobile);
  const canDownloadNonWatermarkedComps = useSelector(
    selectCanDownloadNonWatermarkedComps
  );
  const canDownloadPreviews = useSelector(selectCanDownloadPreviews);
  const selectedAcquisitionOption = useSelector(selectDetailsAcquisitionOption);
  const [showDownloadPreview, setShowDownloadPreview] = useState(false);

  // i.e. AbstractItemDetailsContainer.downloadWithOrWithoutWatermarks
  const downloadWatermarkedPreview = () => {
    window.location.href = getPreviewUrl(stockItem.id, stockItem.contentClass);
  };

  const downloadNonWatermarkedPreview = async () => {
    const dataBridge = await SiteConstants.buildInstance().getDataBridge();

    dataBridge.downloadItem(
      selectedAcquisitionOption.downloadUrl,
      selectedAcquisitionOption.downloadAjaxUrl,
      null,
      dispatch,
      null
    );
  };

  const handleClickPreviewButton = () => {
    setShowDownloadPreview(!showDownloadPreview);
  };

  const freePreviewHandler = () => {
    Telemetry.increment(telemetryKey);

    if (isEnterpriseMember && canDownloadNonWatermarkedComps) {
      return downloadNonWatermarkedPreview();
    }
    return downloadWatermarkedPreview();
  };

  if (children) {
    return children(freePreviewHandler);
  }

  if (canDownloadPreviews) {
    return (
      <PreviewButton
        previewHandler={freePreviewHandler}
        iconOnLeft={iconOnLeft}
        previewText={previewText}
      />
    );
  }

  return (
    <Popper
      placement={isMobile ? Placements.Bottom : Placements.Left}
      strategy="fixed"
      hideContent={() => setShowDownloadPreview(false)}
      hideOnScroll={false}
      popperClasses="bg-white shadow-popover border border-gray-300 border-solid rounded"
      showContent={showDownloadPreview}
      triggerElement={(referenceElement) => (
        <PreviewButton
          iconOnLeft={iconOnLeft}
          previewText={previewText}
          previewHandler={handleClickPreviewButton}
          referenceElement={referenceElement}
        />
      )}
    >
      <SignUpForFeaturePopover
        postSignUpUrl={`${stockItem.detailsUrl}?preview=1`}
        tagLine="Want to download a preview?"
        type="preview"
      />
    </Popper>
  );
};

export default StockItemDownloadButton;
